import {
  ConditionLevel,
  DeferredMaintenanceItem,
  FinancialInputs,
  MajorSystemProfile,
  MinorCapitalItem,
  ModelResult,
  PriorityLevel,
  Property,
  PropertyRiskProfile,
  RiskAction,
  RiskSummary,
  StructuralConditionLevel,
  StructuralConditionProfile
} from '../types';
import { buildModel, calculateReserveCapital } from './financials';

export type FinancialSnapshotScoreInputs = {
  monthlyFreeCashFlow: number;
  cashOnCashReturn: number | null;
  noiGrowthRate?: number | null;
  overallRiskScore: number;
  debtPressure: number;
  liquidityRisk: number;
  capRate: number | null;
  totalCashInvested: number;
};

export type FinancialSnapshotScores = {
  performanceScore: number;
  stabilityScore: number;
  efficiencyScore: number;
};

const majorSystemLabels = {
  roof: 'Roof',
  hvac: 'HVAC',
  waterHeater: 'Water Heater'
} as const;

const structuralLabels = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  foundation: 'Foundation',
  windows: 'Windows',
  flooring: 'Flooring',
  exterior: 'Exterior'
} as const;

const conditionWeight: Record<ConditionLevel, number> = {
  excellent: 0.1,
  good: 0.3,
  watch: 0.65,
  critical: 1
};

const structuralWeight: Record<StructuralConditionLevel, number> = {
  sound: 0.1,
  monitor: 0.35,
  moderate: 0.7,
  severe: 1
};

const priorityWeight: Record<PriorityLevel, number> = {
  low: 0.35,
  medium: 0.65,
  high: 1
};

const uid = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
const createScenarioItem = (value = 0, appliesInYear = 1, notes = '') => ({ value, appliesInYear, notes });

export const createDefaultRiskProfile = (): PropertyRiskProfile => ({
  majorSystems: {
    roof: { key: 'roof', label: majorSystemLabels.roof, age: 0, expectedLife: 25, replacementCost: 0, condition: 'good', priority: 'low', notes: '' },
    hvac: { key: 'hvac', label: majorSystemLabels.hvac, age: 0, expectedLife: 15, replacementCost: 0, condition: 'good', priority: 'low', notes: '' },
    waterHeater: { key: 'waterHeater', label: majorSystemLabels.waterHeater, age: 0, expectedLife: 10, replacementCost: 0, condition: 'good', priority: 'low', notes: '' }
  },
  structural: {
    plumbing: { key: 'plumbing', label: structuralLabels.plumbing, condition: 'sound', estimatedCost: 0, notes: '' },
    electrical: { key: 'electrical', label: structuralLabels.electrical, condition: 'sound', estimatedCost: 0, notes: '' },
    foundation: { key: 'foundation', label: structuralLabels.foundation, condition: 'sound', estimatedCost: 0, notes: '' },
    windows: { key: 'windows', label: structuralLabels.windows, condition: 'sound', estimatedCost: 0, notes: '' },
    flooring: { key: 'flooring', label: structuralLabels.flooring, condition: 'sound', estimatedCost: 0, notes: '' },
    exterior: { key: 'exterior', label: structuralLabels.exterior, condition: 'sound', estimatedCost: 0, notes: '' }
  },
  minorItems: [],
  deferredMaintenance: [],
  scenario: {
    vacancyMonths: createScenarioItem(),
    turnoverCost: createScenarioItem(),
    handymanCost: createScenarioItem(),
    makeReadyCost: createScenarioItem()
  },
  eventSchedule: [],
  notes: ''
});

export const createConservativeRiskTemplate = (): PropertyRiskProfile => ({
  majorSystems: {
    roof: { key: 'roof', label: majorSystemLabels.roof, age: 0, expectedLife: 25, replacementCost: 15000, condition: 'good', priority: 'medium', notes: '' },
    hvac: { key: 'hvac', label: majorSystemLabels.hvac, age: 0, expectedLife: 15, replacementCost: 7000, condition: 'good', priority: 'medium', notes: '' },
    waterHeater: { key: 'waterHeater', label: majorSystemLabels.waterHeater, age: 0, expectedLife: 10, replacementCost: 1800, condition: 'good', priority: 'low', notes: '' }
  },
  structural: {
    plumbing: { key: 'plumbing', label: structuralLabels.plumbing, condition: 'sound', estimatedCost: 0, notes: '' },
    electrical: { key: 'electrical', label: structuralLabels.electrical, condition: 'sound', estimatedCost: 0, notes: '' },
    foundation: { key: 'foundation', label: structuralLabels.foundation, condition: 'sound', estimatedCost: 0, notes: '' },
    windows: { key: 'windows', label: structuralLabels.windows, condition: 'sound', estimatedCost: 0, notes: '' },
    flooring: { key: 'flooring', label: structuralLabels.flooring, condition: 'sound', estimatedCost: 0, notes: '' },
    exterior: { key: 'exterior', label: structuralLabels.exterior, condition: 'sound', estimatedCost: 0, notes: '' }
  },
  minorItems: [],
  deferredMaintenance: [],
  scenario: {
    vacancyMonths: createScenarioItem(),
    turnoverCost: createScenarioItem(),
    handymanCost: createScenarioItem(),
    makeReadyCost: createScenarioItem()
  },
  eventSchedule: [],
  notes: ''
});

export const normalizeRiskProfile = (riskProfile?: Partial<PropertyRiskProfile> | null): PropertyRiskProfile => {
  const base = createDefaultRiskProfile();
  if (!riskProfile) return base;

  const scenario = (riskProfile as any).scenario || {};
  const normalizeScenarioItem = (item: any, fallbackYearKey?: string, fallbackNotesKey?: string) => {
    if (item && typeof item === 'object' && 'value' in item) {
      return createScenarioItem(
        Number(item.value || 0),
        Math.max(1, Number(item.appliesInYear || 1)),
        String(item.notes || '')
      );
    }

    return createScenarioItem(
      Number(item || 0),
      Math.max(1, Number((fallbackYearKey && scenario[fallbackYearKey]) || 1)),
      String((fallbackNotesKey && scenario[fallbackNotesKey]) || '')
    );
  };

  return {
    ...base,
    ...riskProfile,
    majorSystems: {
      ...base.majorSystems,
      ...(riskProfile.majorSystems || {})
    },
    structural: {
      ...base.structural,
      ...(riskProfile.structural || {})
    },
    minorItems: [],
    deferredMaintenance: [],
    scenario: {
      vacancyMonths: createScenarioItem(),
      turnoverCost: createScenarioItem(),
      handymanCost: createScenarioItem(),
      makeReadyCost: createScenarioItem()
    },
    eventSchedule: riskProfile.eventSchedule || [],
    notes: riskProfile.notes || ''
  };
};

const monthsUntilDue = (system: MajorSystemProfile): number => {
  const yearsRemaining = Math.max(0, system.expectedLife - system.age);
  return Math.round(yearsRemaining * 12);
};

const structuralExposure = (item: StructuralConditionProfile): number => item.estimatedCost * structuralWeight[item.condition];

const majorSystemExposure = (system: MajorSystemProfile): number => {
  const lifePressure = 1 - Math.min(1, Math.max(0, (system.expectedLife - system.age) / Math.max(1, system.expectedLife)));
  return system.replacementCost * Math.max(conditionWeight[system.condition], lifePressure, priorityWeight[system.priority]);
};

const filterExposureByMonths = (items: Array<{ timingMonths: number; estimatedCost: number }>, maxMonths: number) =>
  items.filter(item => item.timingMonths <= maxMonths).reduce((sum, item) => sum + item.estimatedCost, 0);

const structuralMonths = (condition: StructuralConditionLevel) => {
  if (condition === 'severe') return 6;
  if (condition === 'moderate') return 12;
  if (condition === 'monitor') return 24;
  return 999;
};

const makePriority = (score: number): PriorityLevel => {
  if (score >= 80) return 'high';
  if (score >= 45) return 'medium';
  return 'low';
};

const clampScore = (value: number) => Math.max(0, Math.min(100, Math.round(value)));

const normalizeMonthlyCashFlowScore = (monthlyFreeCashFlow: number) => {
  // Anchor the score to a simple investor-friendly range: -$500/mo feels broken,
  // while +$500/mo or better is strong for this first-look snapshot.
  return ((Math.max(-500, Math.min(500, monthlyFreeCashFlow)) + 500) / 1000) * 100;
};

const normalizeCashOnCashScore = (cashOnCashReturn: number | null) => {
  if (cashOnCashReturn === null || !Number.isFinite(cashOnCashReturn)) return 0;
  if (cashOnCashReturn <= 0) return 0;
  if (cashOnCashReturn < 5) return (cashOnCashReturn / 5) * 35;
  if (cashOnCashReturn < 8) return 35 + (((cashOnCashReturn - 5) / 3) * 20);
  if (cashOnCashReturn < 12) return 55 + (((cashOnCashReturn - 8) / 4) * 25);
  if (cashOnCashReturn < 15) return 80 + (((cashOnCashReturn - 12) / 3) * 20);
  return 100;
};

const normalizeCapRateScore = (capRate: number | null) => {
  if (capRate === null || !Number.isFinite(capRate) || capRate <= 0) return 0;
  if (capRate < 4) return (capRate / 4) * 35;
  if (capRate < 6) return 35 + (((capRate - 4) / 2) * 25);
  if (capRate < 8) return 60 + (((capRate - 6) / 2) * 20);
  if (capRate < 10) return 80 + (((capRate - 8) / 2) * 15);
  return 95;
};

export const computePerformanceScore = ({
  monthlyFreeCashFlow,
  cashOnCashReturn,
  noiGrowthRate
}: Pick<FinancialSnapshotScoreInputs, 'monthlyFreeCashFlow' | 'cashOnCashReturn' | 'noiGrowthRate'>): number => {
  const cashFlowScore = normalizeMonthlyCashFlowScore(monthlyFreeCashFlow);
  const cocAdjustment = (normalizeCashOnCashScore(cashOnCashReturn) - 50) * 0.18;
  const noiAdjustment = noiGrowthRate === null || noiGrowthRate === undefined
    ? 0
    : Math.max(-8, Math.min(8, noiGrowthRate * 2));

  return clampScore(cashFlowScore + cocAdjustment + noiAdjustment);
};

export const computeStabilityScore = ({
  overallRiskScore,
  debtPressure,
  liquidityRisk
}: Pick<FinancialSnapshotScoreInputs, 'overallRiskScore' | 'debtPressure' | 'liquidityRisk'>): number => {
  const baseScore = 100 - overallRiskScore;
  const debtPenalty = debtPressure > 0.8 ? Math.min(20, (debtPressure - 0.8) * 100) : 0;
  const liquidityPenalty = liquidityRisk > 50 ? Math.min(20, (liquidityRisk - 50) * 0.4) : 0;

  return clampScore(baseScore - debtPenalty - liquidityPenalty);
};

export const computeEfficiencyScore = ({
  cashOnCashReturn,
  capRate,
  totalCashInvested
}: Pick<FinancialSnapshotScoreInputs, 'cashOnCashReturn' | 'capRate' | 'totalCashInvested'>): number => {
  if (!Number.isFinite(totalCashInvested) || totalCashInvested <= 0) return 0;

  const cocScore = normalizeCashOnCashScore(cashOnCashReturn);
  const capRateScore = normalizeCapRateScore(capRate);

  return clampScore((cocScore * 0.65) + (capRateScore * 0.35));
};

export const computeFinancialSnapshotScores = (inputs: FinancialSnapshotScoreInputs): FinancialSnapshotScores => ({
  performanceScore: computePerformanceScore(inputs),
  stabilityScore: computeStabilityScore(inputs),
  efficiencyScore: computeEfficiencyScore(inputs)
});

export const migrateLegacyRiskProfile = (legacyInputs: Record<string, any>): PropertyRiskProfile => {
  const profile = createDefaultRiskProfile();
  if (!legacyInputs || typeof legacyInputs !== 'object') return profile;

  profile.majorSystems.roof.age = Number(legacyInputs.roofAge || 0);
  profile.majorSystems.roof.expectedLife = Number(legacyInputs.roofExpectedLife || 25);
  profile.majorSystems.roof.replacementCost = Number(legacyInputs.roofReplacementCost || 0);

  profile.majorSystems.hvac.age = Number(legacyInputs.hvacAge || 0);
  profile.majorSystems.hvac.expectedLife = Number(legacyInputs.hvacExpectedLife || 15);
  profile.majorSystems.hvac.replacementCost = Number(legacyInputs.hvacReplacementCost || 0);

  profile.majorSystems.waterHeater.age = Number(legacyInputs.waterHeaterAge || 0);
  profile.majorSystems.waterHeater.expectedLife = Number(legacyInputs.waterHeaterExpectedLife || 10);
  profile.majorSystems.waterHeater.replacementCost = Number(legacyInputs.waterHeaterReplacementCost || 0);

  const plumbing = legacyInputs.plumbingCondition === 'old' ? 'moderate' : legacyInputs.plumbingCondition === 'average' ? 'monitor' : 'sound';
  const electrical = legacyInputs.electricalCondition === 'old' ? 'moderate' : legacyInputs.electricalCondition === 'average' ? 'monitor' : 'sound';
  const foundation = legacyInputs.foundationCondition === 'major' ? 'severe' : legacyInputs.foundationCondition === 'minor' ? 'moderate' : 'sound';
  profile.structural.plumbing.condition = plumbing;
  profile.structural.electrical.condition = electrical;
  profile.structural.foundation.condition = foundation;
  profile.structural.plumbing.estimatedCost = plumbing === 'moderate' ? 4000 : 0;
  profile.structural.electrical.estimatedCost = electrical === 'moderate' ? 5000 : 0;
  profile.structural.foundation.estimatedCost = foundation === 'severe' ? 15000 : foundation === 'moderate' ? 6000 : 0;

  if (legacyInputs.vacancyMonths) profile.scenario.vacancyMonths = createScenarioItem(Number(legacyInputs.vacancyMonths || 0), 1, '');
  if (legacyInputs.turnoverCost) profile.scenario.turnoverCost = createScenarioItem(Number(legacyInputs.turnoverCost || 0), 1, '');
  if (legacyInputs.monthlyHandyman) profile.scenario.handymanCost = createScenarioItem(Number(legacyInputs.monthlyHandyman || 0) * 12, 1, '');
  if (legacyInputs.makeReadyCost) profile.scenario.makeReadyCost = createScenarioItem(Number(legacyInputs.makeReadyCost || 0), 1, '');

  return profile;
};

export const computeRiskSummary = (
  inputs: FinancialInputs,
  riskProfile: PropertyRiskProfile,
  modelResult: ModelResult
): RiskSummary => {
  const firstYearMonths = modelResult.monthlyProjection.slice(0, 12);
  const monthlyFreeCashFlow = firstYearMonths.reduce((sum, row) => sum + row.endingCashFlow, 0) / Math.max(1, firstYearMonths.length);
  const monthlyFixedOutflow = firstYearMonths.reduce((sum, row) => sum + row.propertyTaxes + row.insurance + row.hoa + row.propertyManagement + row.routineMaintenance + row.utilities + row.totalDebtService, 0) / Math.max(1, firstYearMonths.length);
  const monthlyDebtService = firstYearMonths.reduce((sum, row) => sum + row.totalDebtService, 0) / Math.max(1, firstYearMonths.length);
  const monthlyTaxes = firstYearMonths.reduce((sum, row) => sum + row.propertyTaxes, 0) / Math.max(1, firstYearMonths.length);
  const monthlyInsurance = firstYearMonths.reduce((sum, row) => sum + row.insurance, 0) / Math.max(1, firstYearMonths.length);
  const monthlyHOA = firstYearMonths.reduce((sum, row) => sum + row.hoa, 0) / Math.max(1, firstYearMonths.length);
  const reserveCapital = calculateReserveCapital(inputs);
  const vacancyMonthsYear1 = Math.round((inputs.vacancyRate || 0) / 100 * 12);
  const vacancyMonths24 = vacancyMonthsYear1;
  const turnoverExposure12 = 0;
  const turnoverExposure24 = 0;
  const makeReadyExposure12 = 0;
  const makeReadyExposure24 = 0;
  const handymanExposure12 = 0;
  const handymanExposure24 = 0;

  const majorSystems = Object.values(riskProfile.majorSystems);
  const majorSystemExposure12 = majorSystems
    .filter(system => monthsUntilDue(system) <= 12 || system.condition === 'critical')
    .reduce((sum, system) => sum + majorSystemExposure(system), 0);
  const majorSystemExposure24 = majorSystems
    .filter(system => monthsUntilDue(system) <= 24 || system.condition === 'critical' || system.condition === 'watch')
    .reduce((sum, system) => sum + majorSystemExposure(system), 0);

  const structuralItems = Object.values(riskProfile.structural);
  const structuralExposure12 = structuralItems
    .filter(item => structuralMonths(item.condition) <= 12)
    .reduce((sum, item) => sum + structuralExposure(item), 0);
  const structuralExposure24 = structuralItems
    .filter(item => structuralMonths(item.condition) <= 24)
    .reduce((sum, item) => sum + structuralExposure(item), 0);

  const minorExposure12 = filterExposureByMonths(riskProfile.minorItems, 12);
  const minorExposure24 = filterExposureByMonths(riskProfile.minorItems, 24);
  const deferredMaintenanceExposure = riskProfile.deferredMaintenance.reduce((sum, item) => sum + item.estimatedCost, 0);
  const turnoverExposure = turnoverExposure24 + makeReadyExposure24;

  const capexNext12Months = majorSystemExposure12 + structuralExposure12 + minorExposure12 + turnoverExposure12 + makeReadyExposure12 + handymanExposure12;
  const capexNext24Months = majorSystemExposure24 + structuralExposure24 + minorExposure24 + turnoverExposure24 + makeReadyExposure24 + handymanExposure24 + deferredMaintenanceExposure;

  const debtPressure = firstYearMonths.reduce((sum, row) => sum + row.totalDebtService, 0) / Math.max(1, firstYearMonths.reduce((sum, row) => sum + (row.totalIncome - row.propertyTaxes - row.insurance - row.hoa - row.propertyManagement - row.routineMaintenance - row.utilities), 0));
  const reserveGap = Math.max(0, capexNext12Months + (monthlyFixedOutflow * vacancyMonthsYear1) - reserveCapital);
  // This represents 6 months of unavoidable fixed costs required to keep the property afloat during income disruption.
  // We intentionally include only fixed obligations here: debt service, property taxes, insurance, and HOA dues.
  const minimumReserve = (monthlyDebtService + monthlyTaxes + monthlyInsurance + monthlyHOA) * 6;
  const recommendedReserve = Math.max(
    monthlyFixedOutflow * (6 + vacancyMonthsYear1),
    capexNext12Months + handymanExposure12,
    turnoverExposure + (monthlyFixedOutflow * 3),
    minimumReserve
  );

  const physicalRisk = Math.min(100, ((majorSystemExposure24 + structuralExposure24 + deferredMaintenanceExposure) / Math.max(1, inputs.purchasePrice)) * 180);
  const liquidityRisk = Math.min(100, (reserveGap / Math.max(1, recommendedReserve)) * 100);
  const operatingRisk = Math.min(100, Math.max(0, (vacancyMonths24 * 12) + (debtPressure * 45) + (monthlyFreeCashFlow <= 0 ? 25 : 0)));
  const minorRisk = Math.min(100, ((minorExposure24 + turnoverExposure) / Math.max(1, recommendedReserve)) * 70);
  const overallRiskScore = Math.min(99, (physicalRisk * 0.35) + (liquidityRisk * 0.25) + (operatingRisk * 0.25) + (minorRisk * 0.15));
  const resilienceScore = Math.max(1, Math.min(99, 100 - overallRiskScore + ((reserveCapital / Math.max(1, recommendedReserve)) * 18)));

  const maintenanceRiskFlags: string[] = [];
  if (structuralItems.some(item => item.condition === 'moderate' || item.condition === 'severe')) {
    maintenanceRiskFlags.push('Structural condition items are contributing real near-term CapEx exposure.');
  }
  if (minorExposure12 > 0) {
    maintenanceRiskFlags.push('Minor items due inside 12 months will pressure make-ready and reserve planning.');
  }
  if (reserveCapital < recommendedReserve) {
    maintenanceRiskFlags.push('Reserve stack is below the recommended cushion for the next 12 months.');
  }

  const actions: RiskAction[] = [];

  majorSystems.forEach(system => {
    const dueMonths = monthsUntilDue(system);
    if (dueMonths <= 24 || system.condition === 'critical' || system.priority === 'high') {
      const riskValue = Math.max(conditionWeight[system.condition] * 100, dueMonths <= 12 ? 85 : dueMonths <= 24 ? 60 : 35);
      actions.push({
        id: `major-${system.key}`,
        title: `${system.label} likely within ${Math.max(1, Math.ceil(dueMonths / 12))} year${dueMonths <= 12 ? '' : 's'}`,
        detail: `${system.label} is marked ${system.condition} with an expected replacement of $${Math.round(system.replacementCost).toLocaleString()}.`,
        priority: makePriority(riskValue)
      });
    }
  });

  structuralItems.forEach(item => {
    if (item.condition === 'moderate' || item.condition === 'severe') {
      actions.push({
        id: `structural-${item.key}`,
        title: `${item.label} marked as ${item.condition} risk`,
        detail: `${item.label} should be budgeted into reserves with an estimated exposure of $${Math.round(structuralExposure(item)).toLocaleString()}.`,
        priority: item.condition === 'severe' ? 'high' : 'medium'
      });
    }
  });

  riskProfile.minorItems.forEach(item => {
    if (item.timingMonths <= 12) {
      actions.push({
        id: `minor-${item.id}`,
        title: `${item.label} likely needed within 12 months`,
        detail: `${item.category} item is forecast at $${Math.round(item.estimatedCost).toLocaleString()} and should be included in your make-ready reserve.`,
        priority: item.timingMonths <= 6 ? 'high' : 'medium'
      });
    }
  });

  riskProfile.deferredMaintenance.forEach((item: DeferredMaintenanceItem) => {
    actions.push({
      id: `deferred-${item.id}`,
      title: `${item.title} is sitting in deferred maintenance`,
      detail: `${item.priority} priority item estimated at $${Math.round(item.estimatedCost).toLocaleString()} due in ${item.timingMonths} months.`,
      priority: item.priority
    });
  });

  if (reserveCapital < recommendedReserve) {
    actions.push({
      id: 'reserve-gap',
      title: 'Reserve position is under target',
      detail: `Add roughly $${Math.round(recommendedReserve - reserveCapital).toLocaleString()} to reach the recommended reserve cushion.`,
      priority: reserveCapital < recommendedReserve * 0.7 ? 'high' : 'medium'
    });
  }

  if (vacancyMonthsYear1 > 0 && (monthlyFreeCashFlow - (monthlyFixedOutflow * vacancyMonthsYear1)) < 0) {
    actions.push({
      id: 'vacancy-stress',
      title: `${vacancyMonthsYear1} month vacancy in Year 1 would push the property negative`,
      detail: 'Vacancy exposure would consume the current free-cash-flow cushion and should be offset with more reserves or lower debt pressure.',
      priority: 'high'
    });
  }

  const deduped = Array.from(new Map(actions.map(action => [action.id, action])).values())
    .sort((a, b) => priorityWeight[b.priority] - priorityWeight[a.priority])
    .slice(0, 8);

  return {
    overallRiskScore,
    resilienceScore,
    liquidityRisk,
    recommendedReserve,
    currentReserve: reserveCapital,
    reserveGap,
    runwayMonths: reserveCapital / Math.max(1, monthlyFixedOutflow),
    monthlyFixedOutflow,
    capexNext12Months,
    capexNext24Months,
    majorSystemExposure12,
    majorSystemExposure24,
    structuralExposure12,
    structuralExposure24,
    minorExposure12,
    minorExposure24,
    deferredMaintenanceExposure,
    turnoverExposure,
    debtPressure,
    monthlyFreeCashFlow,
    deferredMaintenanceCount: riskProfile.deferredMaintenance.length,
    maintenanceRiskFlags,
    actions: deduped
  };
};

export const getPortfolioRiskRankings = (properties: Property[]) => {
  const scored = properties.map((property) => {
    const model = buildModel(
      property.currentYearInputs || property.inputs,
      property.riskProfile,
      undefined,
      { baseYear: property.baseYear, historicalActuals: property.historicalActuals },
      { modelEvents: property.modelEvents || [] }
    ) as ModelResult;
    const summary = computeRiskSummary(property.currentYearInputs || property.inputs, property.riskProfile, model);

    return { property, summary };
  });

  if (!scored.length) return null;

  return {
    highestRisk: [...scored].sort((a, b) => b.summary.overallRiskScore - a.summary.overallRiskScore)[0],
    lowestResilience: [...scored].sort((a, b) => a.summary.resilienceScore - b.summary.resilienceScore)[0],
    highestExposure: [...scored].sort((a, b) => b.summary.capexNext24Months - a.summary.capexNext24Months)[0]
  };
};
